import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Chip from '@mui/material/Chip'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

export interface Organisation {
  id: string
  name: string
  description: string
  stakeholderType: string[]
  website: string
}

interface OrganisationCardProps {
  org: Organisation
}

const typeColors: Record<string, { bg: string; color: string }> = {
  'Funders': { bg: 'rgba(184, 115, 51, 0.2)', color: '#9A5F22' },
  'Research': { bg: 'rgba(138, 154, 91, 0.22)', color: '#6B7A42' },
  'Technical Assistance': { bg: 'rgba(184, 115, 51, 0.16)', color: '#9A5F22' },
  'Service Providers': { bg: 'rgba(11, 32, 39, 0.1)', color: '#2A4A55' },
  'Regenerative Farmers': { bg: 'rgba(138, 154, 91, 0.2)', color: '#6B7A42' },
  'Certification Systems': { bg: 'rgba(138, 154, 91, 0.16)', color: '#6B7A42' },
}

export default function OrganisationCard({ org }: OrganisationCardProps) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 18px 34px rgba(27, 31, 29, 0.12)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 3 }}>
        <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {org.stakeholderType.map((t) => {
            const style = typeColors[t] || { bg: '#f5f5f5', color: '#616161' }
            return (
              <Chip
                key={t}
                label={t}
                size="small"
                sx={{
                  backgroundColor: style.bg,
                  color: style.color,
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 22,
                }}
              />
            )
          })}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.3,
            mb: 1.5,
          }}
        >
          {org.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', lineHeight: 1.65 }}
        >
          {org.description}
        </Typography>
      </CardContent>
      {org.website && (
        <CardActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Link
            href={org.website}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'primary.main',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            Visit website <OpenInNewIcon sx={{ fontSize: 13 }} />
          </Link>
        </CardActions>
      )}
    </Card>
  )
}
